import { Component, effect, inject, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Popover } from 'primeng/popover';
import { ButtonModule } from 'primeng/button';
import { SelectButton } from 'primeng/selectbutton';
import { TooltipModule } from 'primeng/tooltip';
import { UserService } from '@core/services/user.service';
import { AuthService } from '@core/services/auth.service';
import { LanguageService } from '@core/services/language.service';
import { ThemeService, ThemeMode } from '@core/services/theme.service';

@Component({
  selector: 'app-profile-menu',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    Popover,
    ButtonModule,
    SelectButton,
    TooltipModule,
  ],
  templateUrl: './profile-menu.component.html',
  styleUrl: './profile-menu.component.scss',
})
export class ProfileMenuComponent {
  @ViewChild('panel') panel!: Popover;

  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly authService = inject(AuthService);
  private readonly languageService = inject(LanguageService);
  private readonly themeService = inject(ThemeService);

  readonly themeOptions = [
    { label: 'Claro',   value: 'light',  icon: 'pi pi-sun'     },
    { label: 'Oscuro',  value: 'dark',   icon: 'pi pi-moon'    },
    { label: 'Sistema', value: 'system', icon: 'pi pi-desktop' },
  ];

  selectedTheme: ThemeMode = this.themeService.mode();

  onThemeChange(mode: ThemeMode): void {
    this.themeService.setMode(mode);
  }

  isLanguageMenuOpen = false;
  currentLanguage = 'es';
  languageOptions = [
    { label: 'Español', value: 'es' },
    { label: 'English', value: 'en' },
  ];

  get userData() {
    return this.userService.getUserData();
  }
  get userName() {
    return this.userService.userName();
  }

  get fullName(): string {
    return this.userData?.fullName ?? this.userName ?? 'Usuario';
  }

  get email(): string {
    return this.userData?.email ?? this.userName ?? '';
  }

  get cargo(): string {
    return this.userData?.mainRole ?? '';
  }

  get initials(): string {
    const parts = this.fullName.trim().split(' ').filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return this.fullName.substring(0, 2).toUpperCase();
  }

  constructor() {
    effect(() => {
      this.currentLanguage = this.languageService.currentLanguage();
    });
  }

  toggle(event: MouseEvent): void {
    this.panel.toggle(event);
  }

  navigateTo(route: string): void {
    this.panel.hide();
    this.router.navigate([route]);
  }

  logout(): void {
    this.panel.hide();
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  getCurrentLanguageName(): string {
    return this.currentLanguage === 'es' ? 'Español' : 'English';
  }

  toggleLanguageMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.isLanguageMenuOpen = !this.isLanguageMenuOpen;
  }

  onLanguageSelected(value: string): void {
    this.languageService.changeLanguage(value);
    this.isLanguageMenuOpen = false;
  }
}
